import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import {
  Check, Sparkles, UserPlus, CalendarClock, Mail, X, Pencil,
  ShoppingBag, Search, ShoppingCart, Laptop, Building2, Palette, Briefcase, Cpu,
  TrendingUp, Newspaper, Calendar, Clock, BookOpen
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ChoiceOption } from '../../../shared/types/chat'

const CHOICE_ICONS: Record<string, LucideIcon> = {
  UserPlus, CalendarClock, Mail, X, Sparkles, Pencil, ShoppingBag, Search, ShoppingCart,
  Laptop, Building2, Palette, Briefcase, Cpu, TrendingUp, Newspaper, Calendar, Clock, BookOpen
}

interface ChoiceOptionsCardProps {
  options: ChoiceOption[]
  multiSelect?: boolean
  selectedChoice?: string
  selectedChoices?: string[]
  multiChoiceSubmitted?: boolean
  onSelect: (option: ChoiceOption) => void
  onMultiToggle: (optionId: string) => void
  onMultiSubmit: () => void
}

export default function ChoiceOptionsCard({
  options,
  multiSelect,
  selectedChoice,
  selectedChoices = [],
  multiChoiceSubmitted,
  onSelect,
  onMultiToggle,
  onMultiSubmit,
}: ChoiceOptionsCardProps) {
  const { t } = useTranslation('chat')
  if (!multiSelect) {
    const hasSelection = !!selectedChoice
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {options.map((option) => {
          const isApproval = !!option.approvalId
          const Icon: LucideIcon = isApproval ? Check : (CHOICE_ICONS[option.icon || ''] || Sparkles)
          const isSelected = selectedChoice === option.id
          return (
            <motion.button
              key={option.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              whileTap={!hasSelection ? { scale: 0.98 } : {}}
              onClick={() => !hasSelection && onSelect(option)}
              disabled={hasSelection && !isSelected}
              className={`py-1.5 px-3 rounded-full border text-xs font-medium flex items-center gap-1.5 transition-all duration-200 ${
                isSelected
                  ? isApproval
                    ? 'bg-gold text-midnight font-semibold border-gold'
                    : 'border-gold bg-gold/15 text-gold'
                  : hasSelection
                    ? 'border-white/5 bg-white/[0.02] text-pearl/55 cursor-default'
                    : isApproval
                      ? 'bg-gold text-midnight font-semibold border-gold hover:bg-gold-light'
                      : 'border-gold/30 bg-gold/5 text-gold/80 hover:bg-gold/15 hover:border-gold/50 hover:text-gold'
              }`}
            >
              <Icon size={12} className="shrink-0" />
              <span className="text-left">{option.label}</span>
              {isSelected && <Check size={10} className="ml-auto shrink-0" />}
            </motion.button>
          )
        })}
      </div>
    )
  }

  const isSubmitted = !!multiChoiceSubmitted
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {options.map((option) => {
        const Icon: LucideIcon = CHOICE_ICONS[option.icon || ''] || Sparkles
        const isSelected = selectedChoices.includes(option.id)
        return (
          <motion.button
            key={option.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            whileTap={!isSubmitted ? { scale: 0.98 } : {}}
            onClick={() => !isSubmitted && onMultiToggle(option.id)}
            disabled={isSubmitted && !isSelected}
            className={`py-1.5 px-3 rounded-full border text-xs font-medium flex items-center gap-1.5 transition-all duration-200 ${
              isSubmitted
                ? isSelected
                  ? 'border-gold bg-gold/15 text-gold'
                  : 'border-white/5 bg-white/[0.02] text-pearl/55 cursor-default'
                : isSelected
                  ? 'border-gold bg-gold/15 text-gold'
                  : 'border-gold/30 bg-gold/5 text-gold/80 hover:bg-gold/15 hover:border-gold/50 hover:text-gold'
            }`}
          >
            <Icon size={12} className="shrink-0" />
            <span className="text-left">{option.label}</span>
            {isSelected && <Check size={10} className="ml-auto shrink-0" />}
          </motion.button>
        )
      })}
      {!isSubmitted && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={onMultiSubmit}
          disabled={selectedChoices.length === 0}
          className="py-1.5 px-3 rounded-full bg-gold text-midnight text-xs font-semibold hover:bg-gold-light transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
        >
          <Check size={10} />
          {t('choice.done')}
        </motion.button>
      )}
    </div>
  )
}
